/**
 * Client File Brief — the template the adviser completes on the file
 * submission form (the "Summary" field, stored in files.written_instructions).
 *
 * The brief replaced the AI-generated voice-note summary on 29 September
 * 2026: the adviser now writes the instructions for Wouter directly, on the
 * form, before submitting. Three header lines (Client, Date, Work Category)
 * are filled in from the form's own fields and kept in step with them while
 * the adviser types; everything below the header is free text.
 *
 * Pure helpers — no React, no dates of their own. The caller supplies the
 * DD/MM/YYYY date string (built from getTodaySASTDateString() + formatPrepYmd
 * so it is always the SAST calendar date, never browser local time).
 */

// The three placeholder section lines exactly as the template ships them.
// isBriefUntouched() looks for all three unchanged.
const PLACEHOLDER_LINE = '1. xxx';

/**
 * The full template with the three header values filled in (empty string
 * where a value is not known yet).
 */
export function buildClientFileBrief({ clientName = '', dateStr = '', workCategory = '' } = {}) {
  return [
    'CLIENT FILE BRIEF',
    `Client: ${clientName || ''}`,
    `Date: ${dateStr || ''}`,
    `Work Category: ${workCategory || ''}`,
    '',
    'INSTRUCTIONS FOR WOUTER:',
    PLACEHOLDER_LINE,
    '',
    'KEY DOCUMENTS / ATTACHMENTS TO PREPARE:',
    PLACEHOLDER_LINE,
    '',
    'IMPORTANT NOTES:',
    PLACEHOLDER_LINE,
  ].join('\n');
}

/**
 * Keep the header in step with the form: replace ONLY the "Client:",
 * "Date:" and "Work Category:" lines, and only when they sit within the
 * first four lines of the text. Every other line is returned untouched. If
 * the adviser has deleted the header lines the text comes back unchanged.
 */
export function syncBriefHeader(briefText, { clientName = '', dateStr = '', workCategory = '' } = {}) {
  if (typeof briefText !== 'string' || briefText.length === 0) return briefText;
  const lines = briefText.split('\n');
  const headerEnd = Math.min(4, lines.length);
  for (let i = 0; i < headerEnd; i += 1) {
    if (/^Client:/.test(lines[i])) lines[i] = `Client: ${clientName || ''}`;
    else if (/^Date:/.test(lines[i])) lines[i] = `Date: ${dateStr || ''}`;
    else if (/^Work Category:/.test(lines[i])) lines[i] = `Work Category: ${workCategory || ''}`;
  }
  return lines.join('\n');
}

/**
 * True while all three "1. xxx" placeholder lines are still present and
 * unmodified — i.e. the adviser has not written anything in any section
 * yet. The submission form uses this as a soft guard so an empty template
 * is never handed to Wouter.
 */
export function isBriefUntouched(briefText) {
  if (typeof briefText !== 'string') return true;
  const placeholders = briefText
    .split('\n')
    .filter((line) => line.trim() === PLACEHOLDER_LINE).length;
  return placeholders >= 3;
}
